import { Box, Button, Dialog, DialogTitle, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import useTabContext from 'common/hooks/useTabContext';
import AssociationDetailsTab from 'common/modules/AssociationDetailsTab';
import PatientBanner from 'common/modules/PatientBanner';
import { enqueueSnackbar } from 'notistack';
import { updateOrderAssociation } from 'store/thunks/ordersListThunk';
import ModifyAssociationsForm from './ModifyAssociationForm';

const ModifyAssociationsButton = ({ mpi, orderId, order, patientData, onSuccess }) => {
  const dispatch = useDispatch();
  const fromOrderHistoryTab = useTabContext()?.tab === 'OrderHistoryTab';
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const partners = useSelector(({ partner, auth }) => partner[auth?.currentTenantId]);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFormSubmit = (values) => {
    setLoading(true);
    const selectedPartner = partners?.find((p) => p.partnerUuid === values.partnerId);
    const partnerId = selectedPartner?.partnerUuid;
    const selectedPractice = values.practiceId;
    const partnerAccountId = selectedPartner?.partnerAccount.find(
      (partnerAccount) => partnerAccount?.partnerAccountId === values?.partnerAccountId
    )?.partnerAccountId;

    dispatch(
      updateOrderAssociation({
        patientId: mpi,
        partnerId,
        practiceId: selectedPractice,
        orderId,
        partnerAccountId: partnerAccountId ?? undefined,
        includeAllFacilities: fromOrderHistoryTab,
      })
    ).then((isUpdated) => {
      setLoading(false);
      if (isUpdated) {
        enqueueSnackbar({
          variant: 'success',
          message: 'Order associations modified successfully',
        });
        handleClose();
        onSuccess?.();
      }
    });
  };

  return (
    <>
      <Button
        variant='outlined'
        color='secondary'
        size='small'
        onClick={handleOpen}
        sx={{
          px: 1,
          minHeight: 30,
          fontSize: '0.75rem',
          lineHeight: 'normal',
        }}
      >
        <Typography variant='small' color='secondary'>
          Modify Associations
        </Typography>
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle sx={{ mt: '10px', mb: 0, pb: 0, fontWeight: 600 }}>
          Modify Associations
        </DialogTitle>
        <Box sx={{ m: '20px' }}>
          <PatientBanner
            mpi={mpi}
            firstName={patientData?.firstName}
            lastName={patientData?.lastName}
            dob={patientData?.dob}
            email={patientData?.email}
            gender={patientData?.gender}
          />
        </Box>
        <Box>
          <AssociationDetailsTab orderId={orderId} patientId={mpi} order={order} />
        </Box>

        <Form onSubmit={handleFormSubmit}>
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <ModifyAssociationsForm handleClose={handleClose} loading={loading} />
            </form>
          )}
        </Form>
      </Dialog>
    </>
  );
};

ModifyAssociationsButton.propTypes = {
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  order: PropTypes.shape({}).isRequired,
  patientData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dob: PropTypes.string,
    email: PropTypes.string,
    gender: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

ModifyAssociationsButton.defaultProps = {
  patientData: null,
  onSuccess: undefined,
};

export default ModifyAssociationsButton;
